import type { Bet, Market } from '@/types/api';
import { formatCents } from '@/utils/money';
import { labelsForBet } from './betLabels';

const STATUS_STYLE: Record<Bet['status'], { label: string; cls: string; dot: string }> = {
  pending: { label: 'Pending', cls: 'text-amber-400', dot: 'bg-amber-400' },
  won: { label: 'Won', cls: 'text-brand-green', dot: 'bg-brand-green' },
  lost: { label: 'Lost', cls: 'text-red-400', dot: 'bg-red-400' },
  void: { label: 'Void', cls: 'text-white/40', dot: 'bg-white/40' },
};

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/* One bet, the prototype's "My bets" row (stakeholder feedback 2026-10-18,
   items 4 and 5):

   Single — sport + status; the selection; the EVENT (race meeting or match)
   and market with the odds; the event's scheduled start; stake and result.

   Multi — "Multi · N legs" + status; then one line per leg: selection, the
   event it is on, the leg's odds and its own status dot; then the combined
   odds; stake and result.

   Labels come from the board when it still holds the market, otherwise from
   the durable labels GET /bets carries since 0051 (per leg on a multi). The
   result is the money that moved for the customer: +profit on a win, −stake
   on a loss, stake back on a void. The fee is NOT on the row — it is
   disclosed as a running total on the account. */
export function BetRow({ bet, markets, compact = false }: { bet: Bet; markets: Market[] | undefined; compact?: boolean }) {
  const st = STATUS_STYLE[bet.status];
  const isMulti = bet.kind === 'multi';
  // legs is minItems 1 on both shapes; a single also carries its ids at the top level.
  const first = bet.legs[0] ?? (bet.kind === 'single' ? bet : { marketId: '', selectionId: '' });
  const l = labelsForBet(first, markets);
  const odds = Number(bet.combinedDecimalOdds);
  const result = bet.status === 'won'
    ? { text: `+${formatCents((bet.payoutCents ?? 0) - bet.stakeCents)}`, cls: 'text-brand-green' }
    : bet.status === 'lost'
      ? { text: `−${formatCents(bet.stakeCents)}`, cls: 'text-red-400' }
      : bet.status === 'void'
        ? { text: 'Stake returned', cls: 'text-white/40' }
        : { text: `To return ${formatCents(bet.currentPotentialPayoutCents)}`, cls: 'text-white/55' };
  const placed = fmtWhen(bet.placedAt);
  const startAt = isMulti ? null : (bet.legs[0]?.startAt ?? bet.startAt ?? null);
  const eventLine = [l.eventLabel, l.marketLabel].filter(Boolean).join(' · ');

  return (
    <div className={`${compact ? 'px-4 py-3' : 'rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4'}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/40 truncate">
          {isMulti ? `Multi · ${bet.legs.length} legs` : (l.sport ?? placed)}
        </span>
        <span className={`text-[11px] font-bold inline-flex items-center gap-1.5 ${st.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}</span>
      </div>

      {isMulti ? (
        <>
          <ul className="mt-1.5 flex flex-col gap-1">
            {bet.legs.map((leg) => {
              const ll = labelsForBet(leg, markets);
              const ls = STATUS_STYLE[leg.status];
              return (
                <li key={leg.id} className="flex items-start gap-2 min-w-0">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${ls.dot}`} title={ls.label} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{ll.selectionLabel}</div>
                    <div className="text-[11px] text-white/45 truncate">
                      {[ll.eventLabel, ll.marketLabel].filter(Boolean).join(' · ')}
                      {leg.startAt ? ` · ${fmtWhen(leg.startAt)}` : ''}
                    </div>
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-white/80 shrink-0">@ {Number(leg.decimalOdds).toFixed(2)}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-2 text-xs text-white/55">Combined odds <strong className="text-white tabular-nums">{odds.toFixed(2)}</strong></div>
        </>
      ) : (
        <>
          <div className="font-semibold text-[15px] mt-1 truncate">{l.selectionLabel}</div>
          <div className="text-xs text-white/45 truncate">
            {eventLine}{eventLine ? ' · ' : ''}@ {odds.toFixed(2)}
          </div>
          {startAt && <div className="text-[11px] text-white/35 mt-0.5">Starts {fmtWhen(startAt)}</div>}
        </>
      )}

      <div className="flex items-center justify-between mt-2 text-sm">
        <span className="text-white/55">Stake <strong className="text-white tabular-nums">{formatCents(bet.stakeCents)}</strong></span>
        <span className={`font-semibold tabular-nums ${result.cls}`}>{result.text}</span>
      </div>
      {!compact && (
        <div className="text-[11px] text-white/30 mt-1.5">
          Placed {placed}{bet.settledAt ? ` · settled ${new Date(bet.settledAt).toLocaleDateString('en-AU')}` : ''}
        </div>
      )}
    </div>
  );
}
